/**
 * DaddyTalkLandingPage — dedicated /daddytalk page introducing the
 * feature to candidates + collecting demand signal ahead of the full
 * chat MVP shipping (Milestone 2).
 *
 * For now this is a rich placeholder — hero with Uncle D, "how it
 * works" section, live eligible-recruiter counter, and a waitlist CTA
 * so we can measure interest.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import Brand from "@/components/Brand";
import SeoMeta from "@/components/SeoMeta";
import {
    ChatCircleText,
    Lightning,
    UserCircle,
    Sparkle,
    ArrowRight,
    CheckCircle,
    Briefcase,
    MagnifyingGlass,
} from "@phosphor-icons/react";

export default function DaddyTalkLandingPage() {
    const [count, setCount] = useState(null);
    const [onlineCount, setOnlineCount] = useState(0);
    const [recruiters, setRecruiters] = useState([]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const res = await api.get(
                    "/public/daddytalk/eligible-recruiters?workspace_id=ws_default_0000000000000000",
                );
                if (!cancelled) {
                    setCount(res?.data?.total ?? 0);
                    setOnlineCount(res?.data?.online_count ?? 0);
                    setRecruiters(res?.data?.items ?? []);
                }
            } catch {
                if (!cancelled) { setCount(0); setOnlineCount(0); setRecruiters([]); }
            }
        })();
        return () => { cancelled = true; };
    }, []);

    return (
        <div className="min-h-screen bg-cream">
            <SeoMeta
                title="DaddyTalk — Chat directly with recruiters | NaukriDaddy"
                description="Skip the application black hole. Chat directly with hiring recruiters across every agency on NaukriDaddy. Get replies in hours, not weeks."
                canonical="/daddytalk"
            />

            {/* Top nav */}
            <header className="border-b border-ink/10 bg-white">
                <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
                    <Link to="/" aria-label="NaukriDaddy home">
                        <Brand size="md" to={null} />
                    </Link>
                    <div className="flex items-center gap-3">
                        <Link
                            to="/jobs"
                            data-testid="daddytalk-nav-browse-jobs"
                            className="hidden sm:inline-flex items-center gap-1.5 text-xs font-black uppercase tracking-[0.2em] text-ink/70 hover:text-ink transition-colors"
                        >
                            <MagnifyingGlass size={12} weight="bold" />
                            Browse jobs
                        </Link>
                        <Link
                            to="/candidate/signin"
                            data-testid="daddytalk-nav-signin"
                            className="text-xs font-black uppercase tracking-[0.2em] text-ink/70 hover:text-ink transition-colors"
                        >
                            Sign in
                        </Link>
                    </div>
                </div>
            </header>

            {/* Hero */}
            <section className="max-w-6xl mx-auto px-4 py-12 sm:py-20 grid md:grid-cols-2 gap-10 items-center">
                <div>
                    <span
                        data-testid="daddytalk-hero-eyebrow"
                        className="inline-flex items-center gap-1.5 bg-cobalt/10 text-cobalt text-[10px] font-black uppercase tracking-[0.25em] px-3 py-1.5 rounded-full mb-4"
                    >
                        <Sparkle size={12} weight="fill" />
                        New — DaddyTalk
                    </span>
                    <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.05] mb-5">
                        Skip the application black hole.
                        <br />
                        <span className="text-cobalt">Chat directly</span> with the recruiter.
                    </h1>
                    <p className="text-ink/70 text-base sm:text-lg mb-8 leading-relaxed">
                        Message the actual recruiter handling the role. Get replies in hours, not
                        weeks. Fast-track straight to the interview.
                    </p>
                    <div className="flex flex-wrap gap-3 mb-8">
                        <a
                            href="#recruiters"
                            data-testid="daddytalk-start-cta"
                            className="btn-gradient inline-flex items-center gap-2 px-6 py-3 text-[11px] font-black uppercase tracking-[0.2em]"
                        >
                            Browse recruiters
                            <ArrowRight size={12} weight="bold" />
                        </a>
                        <Link
                            to="/jobs"
                            data-testid="daddytalk-browse-jobs-cta"
                            className="inline-flex items-center gap-2 px-6 py-3 border-2 border-ink text-ink text-[11px] font-black uppercase tracking-[0.2em] hover:bg-ink hover:text-white transition-colors"
                        >
                            <Briefcase size={12} weight="bold" />
                            Browse jobs
                        </Link>
                    </div>
                    <p className="text-xs text-ink/50 flex items-center gap-2">
                        <Lightning size={14} weight="fill" className="text-emerald-500" />
                        <span data-testid="daddytalk-live-count">
                            {count === null
                                ? "Loading…"
                                : onlineCount > 0
                                    ? `${onlineCount} recruiter${onlineCount === 1 ? "" : "s"} online right now · ${count} available today`
                                    : count > 0
                                        ? `${count} recruiter${count === 1 ? "" : "s"} available — no one live this second`
                                        : "No recruiters available right now"}
                        </span>
                    </p>
                    <p className="text-[10px] text-ink/40 mt-2">
                        No signup needed to browse. Sign in only when you send your first message.
                    </p>
                </div>
                <div className="relative">
                    <img
                        src="/mascot/daddytalk/hero_full.png"
                        alt="Meet Uncle D — your DaddyTalk guide"
                        data-testid="daddytalk-hero-mascot"
                        className="w-full max-w-[520px] mx-auto"
                        draggable={false}
                    />
                </div>
            </section>

            {/* How it works */}
            <section
                id="how-it-works"
                className="bg-white border-y border-ink/10 py-16 sm:py-20"
            >
                <div className="max-w-6xl mx-auto px-4">
                    <div className="text-center mb-12">
                        <span className="inline-block text-[10px] font-black uppercase tracking-[0.3em] text-cobalt mb-3">
                            How DaddyTalk works
                        </span>
                        <h2 className="font-heading text-3xl sm:text-4xl font-black">
                            Three taps, zero forms.
                        </h2>
                    </div>
                    <div className="grid md:grid-cols-3 gap-8">
                        {[
                            {
                                icon: UserCircle,
                                title: "Pick a role or a recruiter",
                                body: "Browse jobs or the recruiter directory. Every recruiter shows their status — green = accepting chats.",
                                img: "/mascot/daddytalk/avatar_thinking.png",
                            },
                            {
                                icon: ChatCircleText,
                                title: "Chat directly",
                                body: "Message the recruiter handling the role. No forms, no black-hole applications. Replies in hours.",
                                img: "/mascot/daddytalk/avatar_hello.png",
                            },
                            {
                                icon: CheckCircle,
                                title: "Get fast-tracked",
                                body: "Recruiter loves your profile? They fast-track you to the hiring manager — skip the queue entirely.",
                                img: "/mascot/daddytalk/avatar_celebrating.png",
                            },
                        ].map(({ icon: Icon, title, body, img }, i) => (
                            <div
                                key={i}
                                data-testid={`daddytalk-step-${i + 1}`}
                                className="bg-cream p-6 border-2 border-ink/10 hover:border-cobalt transition-colors"
                            >
                                <img
                                    src={img}
                                    alt=""
                                    className="w-24 h-24 rounded-full mx-auto mb-4 border-4 border-white shadow-md"
                                    aria-hidden="true"
                                />
                                <div className="flex items-center justify-center gap-2 mb-3">
                                    <Icon size={16} weight="bold" className="text-cobalt" />
                                    <span className="text-[10px] font-black uppercase tracking-[0.25em] text-cobalt">
                                        Step {i + 1}
                                    </span>
                                </div>
                                <h3 className="font-heading text-lg font-bold mb-2 text-center">
                                    {title}
                                </h3>
                                <p className="text-ink/70 text-sm text-center leading-relaxed">
                                    {body}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Recruiter Directory Strip — live, anonymous browsing */}
            <section
                id="recruiters"
                data-testid="daddytalk-recruiter-directory"
                className="max-w-6xl mx-auto px-4 py-16 sm:py-20"
            >
                <div className="flex items-end justify-between mb-8 flex-wrap gap-3">
                    <div>
                        <span className="inline-block text-[10px] font-black uppercase tracking-[0.3em] text-cobalt mb-2">
                            Recruiters accepting chats now
                        </span>
                        <h2 className="font-heading text-3xl sm:text-4xl font-black">
                            Live directory
                        </h2>
                    </div>
                    <p className="text-xs text-ink/50 flex items-center gap-1.5">
                        <span className="relative flex h-2 w-2">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-500 opacity-75" />
                            <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
                        </span>
                        Updated in real time
                    </p>
                </div>

                {recruiters.length === 0 ? (
                    <div
                        data-testid="daddytalk-directory-empty"
                        className="bg-white border-2 border-dashed border-ink/15 p-10 text-center"
                    >
                        <img
                            src="/mascot/daddytalk/avatar_thinking.png"
                            alt=""
                            className="w-24 h-24 rounded-full mx-auto mb-4 border-4 border-white shadow-md"
                        />
                        <h3 className="font-heading text-lg font-bold mb-2">
                            No recruiters online right now
                        </h3>
                        <p className="text-ink/60 text-sm mb-4 max-w-md mx-auto">
                            Our team is offline. Drop your CV or browse open jobs and we'll
                            reach out to you within 24 hours.
                        </p>
                        <div className="flex flex-wrap gap-2 justify-center">
                            <Link
                                to="/jobs"
                                className="inline-flex items-center gap-2 px-4 py-2 bg-cobalt text-white text-[11px] font-black uppercase tracking-[0.2em] hover:bg-cobalt-dark transition-colors"
                            >
                                <Briefcase size={12} weight="bold" />
                                Browse jobs
                            </Link>
                            <Link
                                to="/rate-my-cv"
                                className="inline-flex items-center gap-2 px-4 py-2 border-2 border-ink text-ink text-[11px] font-black uppercase tracking-[0.2em] hover:bg-ink hover:text-white transition-colors"
                            >
                                Rate my CV
                            </Link>
                        </div>
                    </div>
                ) : (
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                        {recruiters.map((r) => (
                            <div
                                key={r.id}
                                data-testid={`daddytalk-recruiter-card-${r.id}`}
                                data-online={r.is_online ? "1" : "0"}
                                className="bg-white p-5 border-2 border-ink/10 hover:border-cobalt transition-colors group"
                            >
                                <div className="relative w-16 h-16 mx-auto mb-3">
                                    <img
                                        src="/mascot/daddytalk/avatar_neutral.png"
                                        alt=""
                                        className="w-full h-full rounded-full object-cover border-4 border-cream"
                                        onError={(e) => {
                                            e.currentTarget.src = "/mascot/daddytalk/avatar_hello.png";
                                        }}
                                    />
                                    <span
                                        className={`absolute bottom-0 right-0 w-4 h-4 rounded-full border-2 border-white ${r.is_online ? "bg-emerald-500" : "bg-amber-400"}`}
                                        aria-label={r.is_online ? "Online" : "Away"}
                                    />
                                </div>
                                <h3 className="font-heading text-sm font-bold text-center mb-1 truncate">
                                    {r.name}
                                </h3>
                                <p className="text-[10px] text-ink/50 text-center uppercase tracking-wider mb-1">
                                    {r.role === "hiring_manager" ? "Hiring Manager" : "Recruiter"}
                                </p>
                                <p className={`text-[10px] text-center uppercase tracking-wider mb-3 font-black ${r.is_online ? "text-emerald-600" : "text-amber-600"}`}>
                                    {r.is_online ? "● Online" : "● Away"}
                                </p>
                                <Link
                                    to={`/daddytalk/chat/${r.id}`}
                                    data-testid={`daddytalk-chat-btn-${r.id}`}
                                    className="w-full inline-flex items-center justify-center gap-1.5 bg-cobalt text-white text-[10px] font-black uppercase tracking-wider py-2 hover:bg-cobalt-dark transition-colors"
                                >
                                    <ChatCircleText size={11} weight="bold" />
                                    Message
                                </Link>
                            </div>
                        ))}
                    </div>
                )}

                <p className="text-[10px] text-ink/40 text-center mt-6">
                    You can browse and draft a message without signing in.
                    We'll only ask for your details when you send.
                </p>
            </section>

            {/* Final CTA */}
            <section className="max-w-6xl mx-auto px-4 py-16 sm:py-20 text-center">
                <h2 className="font-heading text-3xl sm:text-4xl font-black mb-4">
                    Applications are dead. Meet Uncle D.
                </h2>
                <p className="text-ink/70 text-base sm:text-lg mb-8 max-w-2xl mx-auto">
                    Skip the forms. Skip the wait. Chat directly with the recruiter who's hiring —
                    right now.
                </p>
                <div className="flex flex-wrap justify-center gap-3">
                    <a
                        href="#recruiters"
                        data-testid="daddytalk-final-cta"
                        className="btn-gradient inline-flex items-center gap-2 px-8 py-4 text-[12px] font-black uppercase tracking-[0.2em]"
                    >
                        See who's available
                        <ArrowRight size={12} weight="bold" />
                    </a>
                    <Link
                        to="/jobs"
                        data-testid="daddytalk-final-browse-jobs"
                        className="inline-flex items-center gap-2 px-8 py-4 border-2 border-ink text-ink text-[12px] font-black uppercase tracking-[0.2em] hover:bg-ink hover:text-white transition-colors"
                    >
                        <Briefcase size={12} weight="bold" />
                        Browse jobs first
                    </Link>
                </div>
                <p className="text-[10px] text-ink/40 mt-6">
                    Free forever · No signup to browse · Sign in only when you send your first message
                </p>
            </section>
        </div>
    );
}
